import type { ErrorEvent, EventHint } from '@sentry/vue';
import { hasChunkReloadFired, isDynamicImportError } from './router/chunkReloadRecovery';

// What Sentry should not hear about.
//
// Roughly half of the open javascript-vue issues were never bugs in this app.
// They still cost quota — ingestion had stopped outright by late August 2026
// — and they bury the issues that are. Three kinds go: a third-party script's
// own crashes, a request that never reached the API, and a stale chunk we
// have already reloaded the page for.

/**
 * Scripts whose crashes are not ours to fix. Cloudflare's RUM beacon calls
 * `Array.prototype.at` on browsers that predate it — `this.i.at is not a
 * function`, JAVASCRIPT-VUE-49/48/4A. Sentry matches these against the URL of
 * the frame that threw.
 */
export const SENTRY_DENY_URLS: RegExp[] = [
  /beacon\.min\.js/i,
  /cloudflareinsights\.com/i,
];

/**
 * A request that never reached the API, in each browser's words
 * (JAVASCRIPT-VUE-6, B, 8, 3B, W, 4C). Anchored on purpose: Chrome's
 * stale-chunk failure opens with the same three words and is NOT
 * connectivity — chunkReloadRecovery.ts handles that one. The failed request
 * still lands in the breadcrumbs of whatever is reported next; only the
 * standalone issue goes.
 */
export const SENTRY_IGNORE_ERRORS: RegExp[] = [
  /^Failed to fetch$/, // Chrome, Edge
  /^Load failed$/, // Safari
  /^NetworkError when attempting to fetch resource\.?$/, // Firefox
];

/**
 * Sentry tests `ignoreErrors` against the exception's value; the same test
 * here keeps the list checkable without booting the SDK.
 */
export function isConnectivityNoise(message: string): boolean {
  return SENTRY_IGNORE_ERRORS.some(pattern => pattern.test(message));
}

/**
 * Builds the `beforeSend` that drops a stale-chunk error once the reload-once
 * recovery has fired for it. The reload re-fetches index.html and the chunk
 * names it references, which is the whole fix; the error it replaced is not
 * worth an issue. One that fired no reload — storage unavailable, or the
 * chunk still missing after the reload — is kept, because then nothing fixed
 * it.
 *
 * The decision waits one macrotask: the preload fallback only decides once
 * the router has had its turn, and Sentry may be asked before that.
 */
export function createRecoveredChunkErrorFilter(
  defer: (fn: () => void) => void = (fn) => { setTimeout(fn, 0); },
): (event: ErrorEvent, hint: EventHint) => PromiseLike<ErrorEvent | null> | ErrorEvent {
  return (event, hint) => {
    if (!isDynamicImportError(hint.originalException)) return event;

    return new Promise((resolve) => {
      defer(() => resolve(hasChunkReloadFired() ? null : event));
    });
  };
}

export const dropRecoveredChunkErrors = createRecoveredChunkErrorFilter();
